import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Trophy, Users, Calendar, Target, LogOut, Eye } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ShareProgressDialog } from "./ShareProgressDialog";
import { ChallengeLeaderboard } from "./ChallengeLeaderboard";
import { ChallengeDetail } from "./ChallengeDetail";
import { useState } from "react";

interface ProgressData {
  completed_days: number;
  total_logged_days: number;
  last_update: string | null;
  daily_values: Array<{
    date: string;
    value: number;
    goal_met: boolean;
  }>;
}

interface Challenge {
  id: string;
  title: string;
  description: string;
  challenge_type: string;
  start_date: string;
  end_date: string;
  goal_criteria: any;
  participant_count?: number;
  progress_data?: ProgressData;
  completion_status?: string;
}

export function ActiveChallenges() {
  const queryClient = useQueryClient();
  const [viewLeaderboard, setViewLeaderboard] = useState<string | null>(null);
  const [viewDetail, setViewDetail] = useState<string | null>(null);

  const { data: user } = useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    },
  });

  const { data: challenges, isLoading } = useQuery({
    queryKey: ['active-challenges', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from('challenge_participants')
        .select(`
          id,
          completion_status,
          progress_data,
          challenge_id,
          group_challenges (
            id,
            title,
            description,
            challenge_type,
            start_date,
            end_date,
            goal_criteria,
            participant_count:challenge_participants(count)
          )
        `)
        .eq('user_id', user.id)
        .in('completion_status', ['active', 'completed'])
        .order('joined_at', { ascending: false });

      if (error) throw error;

      return data.map(item => ({
        ...(item.group_challenges as any),
        participant_count: (item.group_challenges as any).participant_count?.[0]?.count || 0,
        progress_data: item.progress_data ? item.progress_data as unknown as ProgressData : undefined,
        completion_status: item.completion_status,
      })) as Challenge[];
    },
    enabled: !!user?.id,
  });

  const leaveMutation = useMutation({
    mutationFn: async (challengeId: string) => {
      if (!user?.id) throw new Error("Not authenticated");

      const { error } = await supabase
        .from('challenge_participants')
        .update({ completion_status: 'left' })
        .eq('challenge_id', challengeId)
        .eq('user_id', user.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['active-challenges'] });
      toast.success("Left challenge");
    },
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">Loading challenges...</p>
        </CardContent>
      </Card>
    );
  }

  if (!challenges || challenges.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center space-y-3">
            <Trophy className="h-12 w-12 mx-auto text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">
              No active challenges yet. Browse challenges to join one!
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const getDaysRemaining = (endDate: string) => {
    const end = new Date(endDate);
    const now = new Date();
    const diff = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const getProgressPercentage = (challenge: Challenge) => {
    if (!challenge.progress_data) return 0;
    const requiredDays = challenge.goal_criteria?.required_days || 7;
    return Math.min(100, (challenge.progress_data.completed_days / requiredDays) * 100);
  };

  const getProgressText = (challenge: Challenge) => {
    if (!challenge.progress_data) return '0/7 days';
    const requiredDays = challenge.goal_criteria?.required_days || 7;
    return `${challenge.progress_data.completed_days}/${requiredDays} days`;
  };

  if (viewDetail) {
    return (
      <ChallengeDetail
        challengeId={viewDetail}
        onBack={() => setViewDetail(null)}
      />
    );
  }

  if (viewLeaderboard) {
    return (
      <ChallengeLeaderboard
        challengeId={viewLeaderboard}
        challengeTitle={challenges?.find(c => c.id === viewLeaderboard)?.title || ''}
      />
    );
  }

  return (
    <div className="grid gap-4">
      {challenges.map((challenge) => {
        const daysRemaining = getDaysRemaining(challenge.end_date);
        const progressPercentage = getProgressPercentage(challenge);

        return (
          <Card key={challenge.id}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Trophy className="h-5 w-5 text-primary" />
                    <CardTitle className="text-lg">{challenge.title}</CardTitle>
                    <Badge variant="secondary" className="capitalize">
                      {challenge.challenge_type}
                    </Badge>
                    {challenge.completion_status === 'completed' && (
                      <Badge variant="default">Completed</Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">{challenge.description}</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Progress Bar */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Progress</span>
                  <span className="font-medium">{getProgressText(challenge)}</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-primary transition-all duration-500"
                    style={{ width: `${progressPercentage}%` }}
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Days Left</p>
                    <p className="font-medium">{daysRemaining}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Participants</p>
                    <p className="font-medium">{challenge.participant_count}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Target className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Streak</p>
                    <p className="font-medium">{challenge.progress_data?.completed_days || 0}</p>
                  </div>
                </div>
              </div>

              {challenge.completion_status === 'completed' ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-center gap-2 p-3 bg-primary/10 rounded-lg text-primary">
                    <Trophy className="h-5 w-5" />
                    <span className="font-medium">Challenge Completed!</span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="default"
                      onClick={() => setViewDetail(challenge.id)}
                      className="flex-1"
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      View Details
                    </Button>
                    <ShareProgressDialog 
                      challengeName={challenge.title} 
                      challengeId={challenge.id} 
                    />
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button
                    variant="default"
                    onClick={() => setViewDetail(challenge.id)}
                    className="flex-1"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    View Details
                  </Button>
                  <Button
                    onClick={() => leaveMutation.mutate(challenge.id)}
                    disabled={leaveMutation.isPending}
                    variant="outline"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Leave
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
